/**
 * Settings > Categories. The three-level tree on the left (Category,
 * Sub-category, Item), and on the right the selected node's settings plus the
 * fields and follow-up questions attached to it. The first row, "Every ticket",
 * holds the fields shown on every ticket on the desk.
 *
 * The tree starts closed (only the top level is listed) so the page stays short.
 * Each branch opens with its arrow and shows how many items it holds; which
 * branches are open is remembered for the browser session. The search box lists
 * matching names and opens the branches that lead to them.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { useCategoriesAll } from '@/lib/useServiceDesk';
import type { SdCategoryAdminNode } from '@/lib/serviceDeskTypes';
import { CategoryForm, type CategoryFormTarget } from './CategoryForm';
import { FieldsPanel } from './FieldsPanel';
import { SdError } from './SdShell';
import { ancestorIds, branchIds, findNode, searchTree } from './categoryTree';
import { SD_BTN_GHOST, SD_CARD, SD_INPUT, SD_MUTED } from './sdStyles';

type Selection = { kind: 'desk' } | { kind: 'node'; id: number } | { kind: 'new'; parentId: number | null };

const OPEN_KEY = 'sd-category-tree-open';

/** The open branches, kept in sessionStorage so a refresh or a visit to another tab keeps them. */
function useOpenBranches(): [Set<number>, (next: Set<number>) => void] {
  const [open, setOpen] = useState<Set<number>>(() => {
    try {
      const raw = window.sessionStorage.getItem(OPEN_KEY);
      const list: unknown = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(list) ? list.filter((n): n is number => typeof n === 'number') : []);
    } catch {
      return new Set();
    }
  });
  const set = useCallback((next: Set<number>) => {
    setOpen(next);
    try {
      window.sessionStorage.setItem(OPEN_KEY, JSON.stringify([...next]));
    } catch {
      // Ignore storage errors.
    }
  }, []);
  return [open, set];
}

function TreeRows({
  nodes,
  depth,
  selectedId,
  onSelect,
  open,
  onToggle,
  visible,
  matches,
}: {
  nodes: SdCategoryAdminNode[];
  depth: number;
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Branches shown open. */
  open: Set<number>;
  onToggle: (id: number) => void;
  /** While searching: the only nodes to list. Null lists everything. */
  visible: Set<number> | null;
  matches: Set<number>;
}) {
  return (
    <ul role={depth === 0 ? 'tree' : 'group'}>
      {[...nodes]
        .filter((n) => !visible || visible.has(n.id))
        .sort((a, b) => a.sort - b.sort)
        .map((n) => {
          const hasKids = n.children.length > 0;
          const isOpen = hasKids && open.has(n.id);
          return (
            <li key={n.id} role="treeitem" aria-expanded={hasKids ? isOpen : undefined} aria-selected={n.id === selectedId}>
              <div
                style={{ paddingLeft: 4 + depth * 16 }}
                className={`flex items-center gap-1 rounded-md ${
                  n.id === selectedId
                    ? 'bg-amp-cream-warm dark:bg-amp-dark-surface-warm'
                    : 'hover:bg-amp-cream dark:hover:bg-amp-dark-surface-warm'
                }`}
              >
                {hasKids ? (
                  <button
                    type="button"
                    onClick={() => onToggle(n.id)}
                    aria-label={`${isOpen ? 'Close' : 'Open'} ${n.name}`}
                    className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded ${SD_MUTED} hover:text-amp-ink dark:hover:text-amp-dark-ink`}
                  >
                    {isOpen ? (
                      <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                    )}
                  </button>
                ) : (
                  <span className="inline-block h-6 w-6 shrink-0" aria-hidden />
                )}
                <button
                  type="button"
                  onClick={() => onSelect(n.id)}
                  aria-current={n.id === selectedId ? 'true' : undefined}
                  className={`flex min-w-0 flex-1 items-center gap-2 py-1.5 pr-2 text-left text-[13px] ${
                    n.id === selectedId ? 'font-semibold' : ''
                  } ${n.is_active ? 'text-amp-ink dark:text-amp-dark-ink' : `${SD_MUTED} line-through`}`}
                >
                  <span
                    className={`min-w-0 flex-1 truncate ${
                      matches.has(n.id) ? 'rounded bg-amber-100 px-0.5 dark:bg-amber-900/40' : ''
                    }`}
                  >
                    {n.name}
                  </span>
                  {!n.is_active && <span className="text-[11px] no-underline">off</span>}
                  {hasKids && (
                    <span className={`text-[11px] no-underline ${SD_MUTED}`} title={`${n.children.length} inside`}>
                      {n.children.length}
                    </span>
                  )}
                  {(n.open_ticket_count ?? 0) > 0 && (
                    <span
                      className="rounded-pill bg-amp-cream-warm px-1.5 text-[11px] dark:bg-amp-dark-surface-warm"
                      title="Open tickets"
                    >
                      {n.open_ticket_count}
                    </span>
                  )}
                </button>
              </div>
              {isOpen && (
                <TreeRows
                  nodes={n.children}
                  depth={depth + 1}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  open={open}
                  onToggle={onToggle}
                  visible={visible}
                  matches={matches}
                />
              )}
            </li>
          );
        })}
    </ul>
  );
}

export function CategoriesTab() {
  const q = useCategoriesAll();
  const tree = useMemo(() => q.data ?? [], [q.data]);
  const [sel, setSel] = useState<Selection>({ kind: 'desk' });
  const [open, setOpen] = useOpenBranches();
  const [search, setSearch] = useState('');
  const found = useMemo(() => searchTree(tree, search), [tree, search]);
  // A search opens the branches that lead to its matches, on top of any the person opened by hand.
  const shownOpen = useMemo(() => (found.visible ? new Set([...open, ...found.opened]) : open), [open, found]);
  const allBranches = useMemo(() => branchIds(tree), [tree]);

  const toggleBranch = (id: number) => {
    const next = new Set(shownOpen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpen(next);
  };

  // Whatever is selected (or about to get a new child) must be visible in the tree.
  const focusId = sel.kind === 'node' ? sel.id : sel.kind === 'new' ? sel.parentId : null;
  const focusIsParent = sel.kind === 'new';
  useEffect(() => {
    if (focusId === null) return;
    const ids = [...ancestorIds(tree, focusId), ...(focusIsParent ? [focusId] : [])];
    if (ids.some((id) => !open.has(id))) setOpen(new Set([...open, ...ids]));
    // Only when the selection or the tree changes; the open set is what this writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, focusIsParent, tree]);

  const selected = sel.kind === 'node' ? findNode(tree, sel.id) : null;
  const parentOfNew = sel.kind === 'new' ? findNode(tree, sel.parentId) : null;

  const siblings = (n: SdCategoryAdminNode): SdCategoryAdminNode[] =>
    (n.parent_id === null ? tree : (findNode(tree, n.parent_id)?.children ?? [])).slice().sort((a, b) => a.sort - b.sort);

  let target: CategoryFormTarget | null = null;
  if (sel.kind === 'new') target = { mode: 'new', parent: parentOfNew };
  else if (selected) target = { mode: 'edit', node: selected };

  return (
    <div className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
      <div className={`${SD_CARD} lg:sticky lg:top-0 lg:max-h-[calc(100dvh-14rem)] lg:self-start lg:overflow-y-auto`}>
        <div className="mb-2">
          <label htmlFor="sd-cat-search" className="sr-only">
            Find a category
          </label>
          <ClearableSearchInput
            id="sd-cat-search"
            value={search}
            onChange={setSearch}
            placeholder="Find a category"
            className={SD_INPUT}
          />
        </div>
        <div className="mb-2 flex gap-3 px-1 text-[12px]">
          <button
            type="button"
            onClick={() => setOpen(new Set([...open, ...allBranches]))}
            className={`${SD_MUTED} underline-offset-2 hover:underline`}
          >
            Open all
          </button>
          <button
            type="button"
            onClick={() => setOpen(new Set())}
            className={`${SD_MUTED} underline-offset-2 hover:underline`}
          >
            Close all
          </button>
        </div>
        <button
          type="button"
          onClick={() => setSel({ kind: 'desk' })}
          aria-current={sel.kind === 'desk' ? 'true' : undefined}
          className={`mb-2 flex w-full rounded-md px-2 py-1.5 text-left text-[13px] font-semibold text-amp-ink dark:text-amp-dark-ink ${
            sel.kind === 'desk' ? 'bg-amp-cream-warm dark:bg-amp-dark-surface-warm' : 'hover:bg-amp-cream dark:hover:bg-amp-dark-surface-warm'
          }`}
        >
          Every ticket (fields on all categories)
        </button>
        {q.isLoading ? (
          <p className={`px-2 text-sm ${SD_MUTED}`}>Loading categories...</p>
        ) : (
          <>
            <TreeRows
              nodes={tree}
              depth={0}
              selectedId={sel.kind === 'node' ? sel.id : null}
              onSelect={(id) => setSel({ kind: 'node', id })}
              open={shownOpen}
              onToggle={toggleBranch}
              visible={found.visible}
              matches={found.matches}
            />
            {found.visible && found.visible.size === 0 && (
              <p className={`px-2 py-2 text-[13px] ${SD_MUTED}`}>Nothing matches "{search.trim()}".</p>
            )}
          </>
        )}
        <div className="mt-3 flex flex-wrap gap-2 border-t border-amp-line pt-3 dark:border-amp-dark-line">
          <button type="button" className={SD_BTN_GHOST} onClick={() => setSel({ kind: 'new', parentId: null })}>
            Add a category
          </button>
          {selected && selected.level < 3 && (
            <button type="button" className={SD_BTN_GHOST} onClick={() => setSel({ kind: 'new', parentId: selected.id })}>
              Add under "{selected.name}"
            </button>
          )}
        </div>
        <SdError error={q.error} what="load the categories" />
      </div>

      <div className={SD_CARD}>
        {sel.kind === 'desk' && (
          <>
            <p className={`mb-1 text-sm ${SD_MUTED}`}>
              Pick a category on the left to change how it works, or add a new one.
            </p>
            <FieldsPanel categoryId={null} title="Fields on every ticket" />
          </>
        )}
        {target && (
          <CategoryForm
            key={target.mode === 'edit' ? `e${target.node.id}` : `n${target.parent?.id ?? 'root'}`}
            target={target}
            tree={tree}
            siblings={selected ? siblings(selected) : []}
            onDone={(id) => setSel(id === null ? { kind: 'desk' } : { kind: 'node', id })}
          />
        )}
        {selected && <FieldsPanel categoryId={selected.id} title={`Fields and questions for "${selected.name}"`} />}
      </div>
    </div>
  );
}
